import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { Filters, createFieldDefs, SubmitEvent, FilterCriterionDTO } from '@workboard/ui-list-filters';
import {
  Popover, ThemeProvider, createTheme, popoverClasses,
  Typography, Switch, Box, TextField, Button, Tooltip,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TuneIcon from '@mui/icons-material/Tune';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import { mergeThemeOptions, themeOptions } from '@workboard/wobo-material';
import { FilterFormProps, Owner, Tag } from '../../types/filter.types';
import { SelectedAssignee, SelectedTag } from '../../types/board.types';
import { STATUS_OPTIONS, RAG_OPTIONS } from '../../utils/constants/filter-form';
import { getActivityIssueTypesFilterOptions, getAiTypeActionLabelPlural } from '../../utils';
import { applyBoardFilters, changeFilterAttribute } from '../../actions/filter.action';
import BoardConst from '../../utils/constants/board';

type FilterOption = {
  label: string;
  value: string;
};

const StyledPopover = styled(Popover)({
  [`.${popoverClasses.paper}`]: {
    borderRadius: '8px',
    padding: '4px 16px 16px',
    marginTop: '5px',
    width: '650px',
    maxHeight: 'calc(100vh - 120px)',
  },
});

const StyledCustomizePopover = styled(Popover)({
  [`.${popoverClasses.paper}`]: {
    borderRadius: '8px',
    padding: '4px 8px 8px',
    marginTop: '5px',
    width: '320px',
    maxHeight: 'calc(100vh - 120px)',
  },
});

const filtersThemeExtensions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          height: 'auto',
          minHeight: '40px',
        },
        input: {
          height: 'auto',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          height: 'auto',
        },
        input: {
          height: '24px',
          padding: '8.5px 14px',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          height: 'auto',
          minHeight: '1.4375em',
        },
      },
    },
  },
};

const filtersTheme = createTheme(mergeThemeOptions(themeOptions, filtersThemeExtensions));

const toTitleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();

const getOptionValues = (options: FilterOption[]) => options.map((option) => String(option.value));

const normalizeStringArray = (value: string[] | string): string[] => (
  Array.isArray(value) ? value.map((item) => String(item)) : []
);

const getAssigneeIds = (assignee: string | string[] | null | undefined): string[] => {
  if (Array.isArray(assignee)) {
    return assignee.filter(Boolean).map((id) => String(id));
  }
  if (!assignee) {
    return [];
  }
  return String(assignee)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);
};

const mapExcludedToIncluded = (excludedValues: string[], allValues: string[]): string[] => {
  if (!excludedValues.length) {
    return allValues;
  }
  return allValues.filter((value) => !excludedValues.includes(value));
};

const mapIncludedToExcluded = (includedValues: string[], allValues: string[]): string[] => {
  if (includedValues.length >= allValues.length) {
    return [];
  }
  return allValues.filter((value) => !includedValues.includes(value));
};

const getCriteriaByField = (criteria: FilterCriterionDTO[], fieldName: string) => (
  criteria.find((item) => item.fieldName === fieldName)
);

const getCriteriaValues = (criteria: FilterCriterionDTO[], fieldName: string): FilterOption[] => {
  const field = getCriteriaByField(criteria, fieldName);
  if (!field || !('values' in field) || !Array.isArray(field.values)) return [];
  return (field.values as (FilterOption | null)[]).filter((v): v is FilterOption => v !== null);
};

const hasCriterion = (criteria: FilterCriterionDTO[], fieldName: string): boolean => (
  criteria.some((item) => item.fieldName === fieldName)
);

// Matches StrongOutlinedButton from the workstreams toolbar
const outlinedButtonSx = {
  fontWeight: 'bold',
  fontSize: 14,
  height: 28,
  padding: '4px 10px',
  borderColor: 'rgba(0, 0, 0, 0.20)',
  color: '#333943',
  borderRadius: '6px',
  textTransform: 'none',
  '& .MuiButton-startIcon': { color: '#687587' },
  '&:hover, &:focus, &.active': {
    borderColor: '#0072ab',
    color: '#0072ab',
    backgroundColor: 'transparent',
    '& .MuiButton-startIcon': { color: '#0072ab' },
  },
  '&.Mui-disabled .MuiButton-startIcon': { color: '#c4cbd7' },
} as const;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #0072ab;
  color: #ffffff !important;
  font-size: 11px;
  line-height: 1;
  width: 18px;
  height: 18px;
  margin-left: 6px;
  border-radius: 50%;
`;

const BOOL_SETTINGS = [
  { key: 'subaction', labelKey: 'Show subtasks' },
  { key: 'highlighted_my_items', labelKey: 'Highlight my actions' },
  { key: 'hide_done_items', labelKey: 'Hide done items' },
  { key: 'showIssueTypeIcon', labelKey: 'Show activity type icons' },
  { key: 'highlightIssuesByType', labelKey: 'Highlight activities by type' },
] as const;

const WorkstreamFilters = (props: FilterFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const filtersRef = useRef<HTMLButtonElement>(null);
  const customizeRef = useRef<HTMLButtonElement>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [doneItemsLocal, setDoneItemsLocal] = useState<string>(String(props.done_items ?? ''));
  const issueTypeFilterOptions = useMemo(() => getActivityIssueTypesFilterOptions(), []);

  // Close popovers on browser back/forward (SITH-724): MUI Popover backdrop
  // otherwise persists across navigation and blocks clicks on the new page.
  useEffect(() => {
    const closeAll = () => {
      setIsFiltersOpen(false);
      setIsCustomizeOpen(false);
    };
    window.addEventListener('popstate', closeAll);
    return () => window.removeEventListener('popstate', closeAll);
  }, []);

  const statusOptions: FilterOption[] = useMemo(() => {
    const preferredOrder = ['2', '1', '0', '4'];
    return preferredOrder.reduce((acc: FilterOption[], id: string) => {
      const option = STATUS_OPTIONS.find((item) => item.id === id);
      if (option) {
        acc.push({ label: toTitleCase(option.title), value: option.id });
      }
      return acc;
    }, []);
  }, []);

  const confidenceOptions: FilterOption[] = useMemo(() => {
    const preferredOrder = ['2', '1', '0'];
    return preferredOrder.reduce((acc: FilterOption[], id: string) => {
      const option = RAG_OPTIONS.find((item) => item.id === id);
      if (option) {
        acc.push({ label: toTitleCase(option.title), value: option.id });
      }
      return acc;
    }, []);
  }, []);

  const priorityOptions: FilterOption[] = useMemo(() => ([
    { label: t('Low'), value: '3' },
    { label: t('Medium'), value: '2' },
    { label: t('High'), value: '1' },
  ]), [t]);

  const typeOptions: FilterOption[] = useMemo(
    () => issueTypeFilterOptions.map((option) => ({
      label: option.title,
      value: option.id,
    })),
    [issueTypeFilterOptions],
  );

  const ownerOptions: FilterOption[] = useMemo(() => {
    const owners: Owner[] = Array.isArray(props.ownerLists) ? props.ownerLists : [];
    return owners.map((owner) => ({
      label: owner.fullName,
      value: String(owner.id),
    }));
  }, [props.ownerLists]);

  const tagOptions: FilterOption[] = useMemo(() => {
    const tags: Tag[] = Array.isArray(props.tagLists) ? props.tagLists : [];
    return tags.map((tag) => ({
      label: tag.label || tag.name,
      value: String(tag.tagId),
    }));
  }, [props.tagLists]);

  // ── Filters section (per workstream) ─────────────────────────────────────

  const filterFieldDefs = useMemo(() => createFieldDefs({
    fields: {
      // eslint-disable-next-line eqeqeq
      ...(props.boardType == BoardConst.WS_BOARD_TYPE_CUSTOM ? {
        status: {
          label: t('Status'),
          type: 'multi-select-button' as const,
          operators: ['includes'],
          options: async () => statusOptions,
        },
      } : {}),
      rating: {
        label: t('Confidence'),
        type: 'multi-select-button',
        operators: ['includes'],
        options: async () => confidenceOptions,
      },
      priority: {
        label: t('Priority'),
        type: 'multi-select-button',
        operators: ['includes'],
        options: async () => priorityOptions,
      },
      issueTypes: {
        label: t('Types'),
        type: 'multi-select-button',
        operators: ['includes'],
        options: async () => typeOptions,
      },
      assignee: {
        label: t('Assignee'),
        type: 'multi-select',
        operators: ['includes'],
        options: async (term: string) => {
          if (!term) return ownerOptions;
          const loweredTerm = term.toLowerCase();
          return ownerOptions.filter((option) => option.label.toLowerCase().includes(loweredTerm));
        },
      },
      tags: {
        label: t('Tags'),
        type: 'multi-select',
        operators: ['includes'],
        options: async (term: string) => {
          if (!term) return tagOptions;
          const lowered = term.toLowerCase();
          return tagOptions.filter((opt) => opt.label.toLowerCase().includes(lowered));
        },
      },
    },
  }), [t, statusOptions, confidenceOptions, priorityOptions, typeOptions, ownerOptions, tagOptions]);

  const filterInitialValues = useMemo(() => {
    const selectedAssignee: SelectedAssignee[] = Array.isArray(props.selected_assignee) ? props.selected_assignee : [];
    const selectedTags: SelectedTag[] = Array.isArray(props.selected_tag) ? props.selected_tag : [];
    const assigneeIds = getAssigneeIds(props.assignee);
    const statusExcluded = normalizeStringArray(props.status);
    const confidenceExcluded = normalizeStringArray(props.rating);
    const priorityExcluded = normalizeStringArray(props.priority);
    const typesExcluded = normalizeStringArray(props.issueTypes);

    const selectedAssigneeValues = selectedAssignee.length
      ? selectedAssignee.map((assignee) => ({
        label: assignee.fullName,
        value: String(assignee.id),
      }))
      : ownerOptions.filter((owner) => assigneeIds.includes(owner.value));

    const criteria = [
      // eslint-disable-next-line eqeqeq
      ...(props.boardType == BoardConst.WS_BOARD_TYPE_CUSTOM ? [{
        fieldName: 'status',
        fieldType: 'multi-select-button' as const,
        operator: 'includes' as const,
        values: statusOptions.filter((option) => mapExcludedToIncluded(statusExcluded, getOptionValues(statusOptions)).includes(option.value)),
        nonRemovable: true,
      }] : []),
      {
        fieldName: 'assignee',
        fieldType: 'multi-select' as const,
        operator: 'includes' as const,
        values: selectedAssigneeValues,
        nonRemovable: true,
      },
      // ── Optional filters — shown only when actively filtering ─────────────
      ...(confidenceExcluded.length > 0 ? [{
        fieldName: 'rating',
        fieldType: 'multi-select-button' as const,
        operator: 'includes' as const,
        values: confidenceOptions.filter((option) => mapExcludedToIncluded(confidenceExcluded, getOptionValues(confidenceOptions)).includes(option.value)),
        nonRemovable: false,
      }] : []),
      ...(priorityExcluded.length > 0 ? [{
        fieldName: 'priority',
        fieldType: 'multi-select-button' as const,
        operator: 'includes' as const,
        values: priorityOptions.filter((option) => mapExcludedToIncluded(priorityExcluded, getOptionValues(priorityOptions)).includes(option.value)),
        nonRemovable: false,
      }] : []),
      ...(typesExcluded.length > 0 ? [{
        fieldName: 'issueTypes',
        fieldType: 'multi-select-button' as const,
        operator: 'includes' as const,
        values: typeOptions.filter((option) => mapExcludedToIncluded(typesExcluded, getOptionValues(typeOptions)).includes(option.value)),
        nonRemovable: false,
      }] : []),
      ...(selectedTags.length > 0 ? [{
        fieldName: 'tags',
        fieldType: 'multi-select' as const,
        operator: 'includes' as const,
        values: selectedTags.map((tag) => ({
          label: tag.label ?? tag.name ?? '',
          value: String(tag.tagId),
        })),
        nonRemovable: false,
      }] : []),
    ];

    return { group: { operator: 'AND' as const, criteria } };
  }, [props, statusOptions, confidenceOptions, priorityOptions, typeOptions, ownerOptions]);

  const closeFilters = () => setIsFiltersOpen(false);

  const handleFiltersSubmit = (event: SubmitEvent) => {
    const criteria: FilterCriterionDTO[] = event?.values?.group?.criteria ?? [];

    // eslint-disable-next-line eqeqeq
    const isCustomBoard = props.boardType == BoardConst.WS_BOARD_TYPE_CUSTOM;
    const selectedStatus = isCustomBoard
      ? getCriteriaValues(criteria, 'status').map((item) => String(item.value))
      : [];
    const selectedAssignees = getCriteriaValues(criteria, 'assignee');

    // Optional filters: only map when the row is present; absent = clear (send [])
    const ratingExcluded = hasCriterion(criteria, 'rating')
      ? mapIncludedToExcluded(getCriteriaValues(criteria, 'rating').map((i) => String(i.value)), getOptionValues(confidenceOptions))
      : [];
    const priorityExcluded = hasCriterion(criteria, 'priority')
      ? mapIncludedToExcluded(getCriteriaValues(criteria, 'priority').map((i) => String(i.value)), getOptionValues(priorityOptions))
      : [];
    const typesExcluded = hasCriterion(criteria, 'issueTypes')
      ? mapIncludedToExcluded(getCriteriaValues(criteria, 'issueTypes').map((i) => String(i.value)), getOptionValues(typeOptions))
      : [];
    const selectedTags = hasCriterion(criteria, 'tags')
      ? getCriteriaValues(criteria, 'tags')
      : [];

    dispatch(applyBoardFilters({
      status: mapIncludedToExcluded(selectedStatus, getOptionValues(statusOptions)),
      rating: ratingExcluded,
      priority: priorityExcluded,
      issueTypes: typesExcluded,
      assignee: selectedAssignees.map((item) => String(item.value)).join(','),
      selected_assignee: selectedAssignees.map((item) => ({
        id: String(item.value),
        fullName: item.label,
      })),
      tags: selectedTags.map((item) => String(item.value)),
      selected_tag: selectedTags.map((item) => ({
        tagId: String(item.value),
        name: item.label,
        label: item.label,
      })),
      tags_operator: 'includes',
    }));

    closeFilters();
  };

  const handleFiltersReset = () => {
    dispatch(applyBoardFilters({
      status: [],
      rating: [],
      priority: [],
      issueTypes: [],
      assignee: '',
      selected_assignee: [],
      tags: [],
      selected_tag: [],
      tags_operator: 'includes',
    }));
    closeFilters();
  };

  // ── Settings section (per user, global) ──────────────────────────────────

  const handleSettingChange = (key: string, value: string) => {
    dispatch(changeFilterAttribute(key, value));
  };

  const handleDoneItemsBlur = () => {
    const cleaned = doneItemsLocal.trim();
    if (/^\d*$/.test(cleaned)) {
      dispatch(changeFilterAttribute('done_items', cleaned));
    }
  };

  const settingsContent = (
    <Box sx={{ py: 0.5 }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        px: 1,
        pb: 0.5,
      }}>
        <Typography variant="subtitle2" fontWeight={700} fontSize={13}>
          {t('Settings')}
        </Typography>
        <Tooltip title={t('Applies to all your Workstreams')} placement="top" arrow>
          <HelpOutlineIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
        </Tooltip>
      </Box>

      {BOOL_SETTINGS.map(({ key, labelKey }) => {
        // Loose equality intentional: Yii1/PDO returns DB integers as strings,
        // so boardType may arrive as "2" (string) instead of 2 (number).
        // eslint-disable-next-line eqeqeq
        if (key === 'hide_done_items' && props.boardType != BoardConst.WS_BOARD_TYPE_CUSTOM) {
          return null;
        }
        return (
        <Box
          key={key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            minHeight: 36,
          }}
        >
          <Typography variant="body2">
            {key === 'highlighted_my_items'
              ? t('Highlight my {{actionLabelPlural}}', { actionLabelPlural: getAiTypeActionLabelPlural().toLowerCase() })
              : t(labelKey)}
          </Typography>
          <Switch
            checked={String(props[key as keyof FilterFormProps]) === '1'}
            onChange={(e) => handleSettingChange(key, e.target.checked ? '1' : '0')}
            size="small"
          />
        </Box>
        );
      })}

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        minHeight: 36,
        mb: 0.5,
      }}>
        <Typography variant="body2">{t('Show done items for (days)')}</Typography>
        <TextField
          type="number"
          size="small"
          value={doneItemsLocal}
          onChange={(e) => setDoneItemsLocal(e.target.value)}
          onBlur={handleDoneItemsBlur}
          onWheel={(e) => (e.target as HTMLElement).blur()}
          inputProps={{ min: 0, step: 1 }}
          sx={{ width: 88 }}
        />
      </Box>
    </Box>
  );

  // ── Badge count (filters only, excludes settings) ─────────────────────────

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    const statusExcluded = normalizeStringArray(props.status);
    const confidenceExcluded = normalizeStringArray(props.rating);
    const priorityExcluded = normalizeStringArray(props.priority);
    const typeExcluded = normalizeStringArray(props.issueTypes);
    const assignees = getAssigneeIds(props.assignee);
    const tags = normalizeStringArray(props.tags);

    // eslint-disable-next-line eqeqeq
    if (props.boardType == BoardConst.WS_BOARD_TYPE_CUSTOM && statusExcluded.length > 0 && statusExcluded.length < statusOptions.length) count++;
    if (confidenceExcluded.length > 0 && confidenceExcluded.length < confidenceOptions.length) count++;
    if (priorityExcluded.length > 0 && priorityExcluded.length < priorityOptions.length) count++;
    if (typeExcluded.length > 0 && typeExcluded.length < typeOptions.length) count++;
    if (assignees.length > 0) count++;
    if (tags.length > 0) count++;

    return count;
  }, [props.status, props.rating, props.priority, props.issueTypes, props.assignee, props.tags, statusOptions.length, confidenceOptions.length, priorityOptions.length, typeOptions.length]);

  const popoverAnchorProps = {
    anchorOrigin: { vertical: 'bottom' as const, horizontal: 'right' as const },
    transformOrigin: { vertical: 'top' as const, horizontal: 'right' as const },
  };

  return (
    <div className="board-filter-wrapper">
      <ThemeProvider theme={filtersTheme}>
        <Box sx={{ position: 'absolute', right: '24px', top: 0, display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* ── Filters trigger ── */}
          <Button
            ref={filtersRef}
            variant="outlined"
            disableElevation
            size="small"
            startIcon={<TuneIcon fontSize="small" />}
            onClick={() => setIsFiltersOpen(true)}
            aria-label={t('Filter options')}
            aria-expanded={isFiltersOpen}
            sx={outlinedButtonSx}
          >
            {t('Filter')}
            {appliedFilterCount > 0 && (
              <FilterBadge aria-label={`${appliedFilterCount} ${t('filters applied')}`}>
                {appliedFilterCount}
              </FilterBadge>
            )}
          </Button>

          {/* ── Customize trigger ── */}
          <Button
            ref={customizeRef}
            variant="outlined"
            disableElevation
            size="small"
            startIcon={<DashboardCustomizeOutlinedIcon fontSize="small" />}
            onClick={() => setIsCustomizeOpen(true)}
            aria-label={t('Customize')}
            aria-expanded={isCustomizeOpen}
            sx={outlinedButtonSx}
          >
            {t('Customize')}
          </Button>
        </Box>

        {/* ── Filters popover ── */}
        <StyledPopover
          anchorEl={filtersRef.current}
          open={isFiltersOpen}
          keepMounted
          onClose={closeFilters}
          {...popoverAnchorProps}
        >
          <Filters
            fieldDefs={filterFieldDefs}
            initialValues={filterInitialValues}
            isApplyDisabled={false}
            allowEmptyFiltersList
            onReset={handleFiltersReset}
            onSubmit={handleFiltersSubmit}
            onClose={closeFilters}
            variant="compact"
          />
        </StyledPopover>

        {/* ── Customize popover ── */}
        <StyledCustomizePopover
          anchorEl={customizeRef.current}
          open={isCustomizeOpen}
          onClose={() => setIsCustomizeOpen(false)}
          {...popoverAnchorProps}
        >
          {settingsContent}
        </StyledCustomizePopover>
      </ThemeProvider>
    </div>
  );
};

export default WorkstreamFilters;
